import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  CardMedia,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { config } from '../config';
import DOMPurify from 'dompurify';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import GitHubIcon from '@mui/icons-material/GitHub';
import CloudIcon from '@mui/icons-material/Cloud';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { getDerivativeImageUrl, getImageUrl } from '../utils/imageUrl';

const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

const scoreColour = (score = 0) => {
  if (score >= 78) return '#76ffb2';
  if (score >= 62) return '#d8ff72';
  if (score >= 45) return '#ffd36f';
  if (score >= 28) return '#ff946f';
  return '#ff6b8a';
};


const githubProjects = [
  {
    name: 'AstroVEdit',
    description: 'Amateur astronomical image editing software — the headline Astro Vino software project.',
    url: 'https://github.com/astro-vino/AstroVEdit',
    tag: 'Image editing'
  },
  {
    name: 'AstroVinoCollimation',
    description: 'Collimation software for ZWO and generic webcam camera workflows.',
    url: 'https://github.com/astro-vino/AstroVinoCollimation',
    tag: 'Telescope setup'
  },
  {
    name: 'ASCOM-AllSkyCondition',
    description: 'ASCOM SafetyMonitor driver using all-sky camera images to assess sky conditions.',
    url: 'https://github.com/astro-vino/ASCOM-AllSkyCondition',
    tag: 'Observatory automation'
  },
  {
    name: 'WeatherTempest / ASCOMTempest',
    description: 'Tempest weather integrations for N.I.N.A. and ASCOM SafetyMonitor observatory workflows.',
    url: 'https://github.com/astro-vino',
    tag: 'Weather integrations'
  }
];

const rigSetups = [
  {
    title: 'Imaging rig',
    text: 'A home for the telescope, camera, mount and filter-wheel setup used for deeper-sky image runs.',
    icon: <CameraAltIcon />
  },
  {
    title: 'Lunar / planetary rig',
    text: 'A separate section for high-frame-rate Moon and planet capture workflows, cameras and comparison tests.',
    icon: <VideoLibraryIcon />
  },
  {
    title: 'Observatory control stack',
    text: 'Weather, safety, automation and local tooling that support the rigs without turning the homepage into a control panel.',
    icon: <CloudIcon />
  }
];

function Home() {
  const [settings, setSettings] = useState({ spaceHeader: '' });
  const [latestUpdates, setLatestUpdates] = useState([]);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [galleryPreview, setGalleryPreview] = useState([]);
  const [nightChart, setNightChart] = useState(null);
  const [skyNewsItems, setSkyNewsItems] = useState([]);
  const [, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, newsRes, galleryRes, nightRes, skyNewsRes] = await Promise.allSettled([
        fetchWithTimeout(config.endpoints.settings),
        fetchWithTimeout(config.endpoints.news),
        fetchWithTimeout(config.endpoints.gallery),
        fetchWithTimeout(`${config.endpoints.nightChart}?mode=astrophotography&name=Northumberland%2C%20England`),
        fetchWithTimeout(`${config.endpoints.skyNews}?limit=3`)
      ]);

      if (settingsRes.status === 'fulfilled' && settingsRes.value.ok) {
        const data = await settingsRes.value.json();
        setSettings(data);
      }

      if (newsRes.status === 'fulfilled' && newsRes.value.ok) {
        const data = await newsRes.value.json();
        setLatestUpdates(data.slice(0, 3));
      }

      if (galleryRes.status === 'fulfilled' && galleryRes.value.ok) {
        const data = await galleryRes.value.json();
        if (data && data.length > 0) {
          const leadImage = data[0];
          setFeaturedImage({
            url: leadImage.mediumUrl || getDerivativeImageUrl(leadImage.url, 'medium') || leadImage.url,
            originalUrl: leadImage.originalUrl || getImageUrl(leadImage.url),
            id: leadImage.id,
            title: leadImage.title || 'Astro Vino capture'
          });
          setGalleryPreview(data.slice(0, 8));
        }
      }

      if (nightRes.status === 'fulfilled' && nightRes.value.ok) {
        setNightChart(await nightRes.value.json());
      }

      if (skyNewsRes.status === 'fulfilled' && skyNewsRes.value.ok) {
        const data = await skyNewsRes.value.json();
        setSkyNewsItems((data.items || []).slice(0, 3));
      }
    } catch (err) {
      console.error('Error fetching homepage data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchData();
    return () => { mounted = false; };
  }, []);

  const extractFirstImage = (content) => {
    if (!content) return null;
    const div = document.createElement('div');
    div.innerHTML = content;
    const img = div.querySelector('img');
    return img ? img.getAttribute('src') : null;
  };

  const createPreview = (content, maxLength = 170) => {
    if (!content) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength).trim()}…`;
  };

  const heroImage = featuredImage?.url || (settings.spaceHeader ? getImageUrl(settings.spaceHeader) : null);
  const heroImages = galleryPreview.length > 0 ? galleryPreview : (featuredImage ? [featuredImage] : []);
  const best = nightChart?.bestWindow;

  return (
    <Box sx={{ background: 'radial-gradient(circle at 10% 0%, rgba(108,54,145,0.26), transparent 36%), radial-gradient(circle at 92% 12%, rgba(245,151,79,0.14), transparent 30%), #080910', pb: 5 }}>
      <motion.Box initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <Box sx={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 12% 10%, rgba(245,151,79,0.16), transparent 28%), radial-gradient(circle at 82% 0%, rgba(108,54,145,0.20), transparent 34%)' }} />
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: { xs: 2.2, md: 3.2 } }}>
            <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
              <Grid item xs={12} lg={8.4}>
                <Paper sx={{ position: 'relative', minHeight: { xs: 390, sm: 460, md: 560 }, overflow: 'hidden', borderRadius: 2.5, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(6,7,14,0.88)', boxShadow: '0 24px 80px rgba(0,0,0,0.42)' }}>
                  {heroImage && (
                    <Box component="img" src={heroImage} alt={featuredImage?.title || 'Astro Vino featured capture'} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(1.08) contrast(1.05)' }} />
                  )}
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,6,12,0.08) 0%, rgba(5,6,12,0.12) 42%, rgba(5,6,12,0.90) 100%)' }} />
                  <Stack spacing={1.2} sx={{ position: 'absolute', left: { xs: 18, md: 30 }, right: { xs: 18, md: 30 }, bottom: { xs: 18, md: 28 } }}>
                    <Typography variant="overline" sx={{ color: 'primary.light', letterSpacing: 2.4, fontWeight: 900, textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}>Latest Astro Vino capture</Typography>
                    <Typography variant="h1" sx={{ fontWeight: 900, letterSpacing: '-0.035em', fontSize: { xs: '2.05rem', sm: '2.8rem', md: '4rem' }, lineHeight: 0.98, maxWidth: 860, textShadow: '0 4px 24px rgba(0,0,0,0.78)' }}>
                      {featuredImage?.title || 'Astrophotography from Northumberland'}
                    </Typography>
                    <Typography color="text.secondary" sx={{ color: 'rgba(255,255,255,0.86)', maxWidth: 720, lineHeight: 1.55, fontSize: { xs: '0.95rem', md: '1.02rem' }, textShadow: '0 2px 12px rgba(0,0,0,0.72)' }}>
                      Your image archive first — lunar, planetary and deep-sky captures, with projects and tools further down the page.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} sx={{ pt: 0.5 }}>
                      <Button component={Link} to={featuredImage?.id ? `/gallery?image=${featuredImage.id}` : '/gallery'} variant="contained" startIcon={<CameraAltIcon />} sx={{ alignSelf: 'flex-start', bgcolor: 'primary.light', color: '#160920', fontWeight: 900, px: 2.25, '&:hover': { bgcolor: '#ffb06f' } }}>
                        Open this capture
                      </Button>
                      <Button component={Link} to="/gallery" variant="outlined" sx={{ alignSelf: 'flex-start', borderColor: 'rgba(255,255,255,0.42)', color: '#fff', fontWeight: 850 }}>
                        View full gallery
                      </Button>
                      <Button component={Link} to="/sky-map" variant="outlined" startIcon={<TravelExploreIcon />} sx={{ alignSelf: 'flex-start', borderColor: 'rgba(114,240,255,0.44)', color: '#dffaff', fontWeight: 850 }}>
                        3D sky map
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} lg={3.6}>
                <Grid container spacing={1.2} sx={{ height: '100%' }}>
                  {heroImages.slice(1, 5).map((image) => (
                    <Grid item xs={6} lg={12} key={image.id}>
                      <Paper component={Link} to={`/gallery?image=${image.id}`} sx={{ position: 'relative', display: 'block', height: { xs: 132, sm: 158, lg: '100%' }, minHeight: { lg: 128 }, overflow: 'hidden', textDecoration: 'none', color: 'inherit', borderRadius: 2, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)' }}>
                        <Box component="img" src={image.mediumUrl || image.thumbnailUrl || getDerivativeImageUrl(image.url, 'medium') || image.url} alt={image.title || 'Astro Vino gallery capture'} loading="lazy" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.25s ease', '&:hover': { transform: 'scale(1.035)' } }} />
                        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 35%, rgba(5,6,12,0.82) 100%)' }} />
                        <Typography sx={{ position: 'absolute', left: 12, right: 12, bottom: 10, fontWeight: 850, fontSize: { xs: '0.78rem', md: '0.86rem' }, lineHeight: 1.15, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                          {image.title || 'Gallery capture'}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 1.35, background: 'rgba(108,54,145,0.14)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
                      <Stack direction="row" spacing={1.1} alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography variant="overline" sx={{ color: 'primary.light', letterSpacing: 1.5, fontWeight: 850 }}>Tonight</Typography>
                          {best ? (
                            <Typography sx={{ fontWeight: 900, color: scoreColour(best.score), fontSize: '1.02rem', lineHeight: 1.1 }}>{best.time} · {best.score}/100</Typography>
                          ) : (
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }}>Loading chart…</Typography>
                          )}
                        </Box>
                        <Button component={Link} to="/night-chart" variant="text" size="small" startIcon={<NightsStayIcon />} sx={{ color: 'text.primary', fontWeight: 850 }}>Chart</Button>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ mt: 3.5 }}>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, background: 'linear-gradient(135deg, rgba(245,151,79,0.10), rgba(255,255,255,0.035))', border: '1px solid rgba(245,151,79,0.13)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 850, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>Equipment & rig setups</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6, maxWidth: 760 }}>
                  A compact front-page summary of the gear behind Astro Vino. The full equipment page now holds the observatory, rigs, scopes, camera-train images and practical setup notes.
                </Typography>
              </Box>
              <Button component={Link} to="/equipment" variant="outlined" size="small" sx={{ color: 'primary.light', borderColor: 'rgba(245,151,79,0.38)' }}>Equipment page</Button>
            </Stack>
            <Grid container spacing={1.5}>
              {rigSetups.map((rig) => (
                <Grid item xs={12} md={4} key={rig.title}>
                  <Card sx={{ height: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <CardContent sx={{ p: 1.8 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.light', mb: 0.9 }}>
                        {rig.icon}
                        <Typography sx={{ fontWeight: 850 }}>{rig.title}</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.83rem', lineHeight: 1.5 }}>{rig.text}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper sx={{ mt: 3, p: { xs: 2, md: 3 }, background: 'linear-gradient(135deg, rgba(25,118,210,0.12), rgba(255,255,255,0.035))', border: '1px solid rgba(255,255,255,0.09)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GitHubIcon sx={{ color: 'primary.light' }} />
                  <Typography variant="h6" sx={{ fontWeight: 850, fontSize: { xs: '1.05rem', md: '1.22rem' } }}>Astro Vino code & tools</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6, maxWidth: 760 }}>
                  Open GitHub projects that support the capture, observatory automation and image-processing side of Astro Vino — especially AstroVEdit.
                </Typography>
              </Box>
              <Button href="https://github.com/astro-vino" target="_blank" rel="noopener noreferrer" variant="outlined" size="small" sx={{ color: 'primary.light', borderColor: 'rgba(144,202,249,0.45)' }}>GitHub profile</Button>
            </Stack>
            <Grid container spacing={1.5}>
              {githubProjects.map((project) => (
                <Grid item xs={12} sm={6} md={3} key={project.name}>
                  <Card component="a" href={project.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', flexDirection: 'column', height: '100%', textDecoration: 'none', color: 'inherit', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', transition: 'border-color 0.2s ease, transform 0.2s ease', '&:hover': { borderColor: 'rgba(144,202,249,0.42)', transform: 'translateY(-2px)' } }}>
                    <CardContent sx={{ flex: 1, p: 1.75 }}>
                      <Chip size="small" label={project.tag} sx={{ mb: 1, height: 22, '& .MuiChip-label': { fontSize: '0.68rem' } }} />
                      <Typography sx={{ fontWeight: 850, fontSize: '0.96rem', lineHeight: 1.25 }}>{project.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.85, fontSize: '0.8rem', lineHeight: 1.45 }}>{project.description}</Typography>
                    </CardContent>
                    <CardActions sx={{ px: 1.75, pt: 0 }}>
                      <Button size="small" sx={{ px: 0, color: 'primary.light' }}>View repository</Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} lg={7}>
              <Paper sx={{ p: { xs: 2, md: 3 }, background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>Latest Astro Vino articles</Typography>
                  <Button component={Link} to="/news" sx={{ color: 'primary.light' }}>All news</Button>
                </Stack>
                <Grid container spacing={2}>
                  {latestUpdates.map((update, index) => {
                    const img = update.mediumUrl || update.image || extractFirstImage(update.content);
                    return (
                      <Grid item xs={12} md={index === 0 ? 12 : 6} key={update.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: { xs: 'column', md: index === 0 ? 'row' : 'column' }, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          {img && <CardMedia component="img" image={img} alt={update.title} sx={{ width: { md: index === 0 ? 300 : '100%' }, height: index === 0 ? 210 : 185, objectFit: 'contain', bgcolor: 'rgba(3,8,18,0.92)', borderRight: { md: index === 0 ? '1px solid rgba(255,255,255,0.07)' : 0 }, borderBottom: '1px solid rgba(255,255,255,0.07)' }} />}
                          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <CardContent sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ color: 'primary.light', textTransform: 'uppercase', letterSpacing: 1 }}>Latest update</Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.5, fontSize: { xs: '0.98rem', md: '1.05rem' } }}>{update.title}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(createPreview(update.content)) }} />
                            </CardContent>
                            <CardActions><Button component={Link} to={`/news/${update.id}`} size="small">Read more</Button></CardActions>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%', background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>Featured capture</Typography>
                  <Button component={Link} to="/gallery" sx={{ color: 'primary.light' }}>Gallery</Button>
                </Stack>
                {featuredImage && (
                  <Card sx={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                    <CardMedia component="img" height="330" image={featuredImage.url} alt="Featured Astro Vino capture" sx={{ objectFit: 'cover' }} />
                    <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                      <Typography variant="body2" color="text.secondary">Optimised preview · original retained for zoom</Typography>
                      <Button component={Link} to={`/gallery?image=${featuredImage.id}`} variant="contained" size="small">See image</Button>
                    </CardActions>
                  </Card>
                )}
                {galleryPreview.length > 0 && (
                  <Grid container spacing={1} sx={{ mt: 1.5 }}>
                    {galleryPreview.map((image) => (
                      <Grid item xs={4} key={image.id}>
                        <Box component={Link} to={`/gallery?image=${image.id}`} sx={{ display: 'block', borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <Box component="img" src={image.thumbnailUrl || getDerivativeImageUrl(image.url, 'thumb') || image.url} alt="Gallery preview" loading="lazy" sx={{ display: 'block', width: '100%', height: 86, objectFit: 'cover' }} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
                {skyNewsItems.length > 0 && (
                  <Box sx={{ mt: 2.25, pt: 1.75, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 850, color: 'primary.light' }}>External astronomy links</Typography>
                      <Chip size="small" label="External links" sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.68rem' } }} />
                    </Stack>
                    <Stack spacing={1.05}>
                      {skyNewsItems.map((item) => {
                        const skyNewsImage = item.thumbnailUrl
                          ? (item.thumbnailUrl.startsWith('http') ? item.thumbnailUrl : `${config.API_URL}${item.thumbnailUrl}`)
                          : item.imageUrl;
                        return (
                        <Box key={item.id} component="a" href={item.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', gap: 1.1, textDecoration: 'none', color: 'inherit', p: 1.1, borderRadius: 1.5, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', '&:hover': { borderColor: 'rgba(245,151,79,0.35)' } }}>
                          {skyNewsImage && (
                            <Box component="img" src={skyNewsImage} alt="" loading="lazy" sx={{ width: 58, height: 48, objectFit: 'cover', borderRadius: 1, flexShrink: 0, background: 'rgba(255,255,255,0.08)' }} />
                          )}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', lineHeight: 1.25 }}>{item.title}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>{item.source} · {new Date(item.publishedAt).toLocaleDateString()}</Typography>
                          </Box>
                        </Box>
                        );
                      })}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Referenced external reading — each link opens the original source.
                    </Typography>
                  </Box>
                )}

              </Paper>
            </Grid>
          </Grid>
        </Container>
      </motion.Box>
    </Box>
  );
}

export default Home;
